import type { Contact, ContactGroup } from "../types";
import { GROUP_COLORS } from "../types";

interface Props {
  contacts: Contact[];
  groups: ContactGroup[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  onEdit: (c: Contact) => void;
  onDelete: (id: string) => void;
  onSendTo: (c: Contact) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}

function getInitialColor(name: string): string {
  const colors = ["from-violet-500 to-violet-600", "from-pink-500 to-pink-600", "from-emerald-500 to-emerald-600", "from-amber-500 to-amber-600", "from-sky-500 to-sky-600"];
  const idx = name.charCodeAt(0) % colors.length;
  return colors[idx];
}

function Pagination({ currentPage, totalPages, onPageChange }: { currentPage: number; totalPages: number; onPageChange: (p: number) => void }) {
  return (
    <div className="border-t border-white/[0.07] px-4 md:px-5 py-3 md:py-3.5 flex items-center justify-between">
      <p className="text-gray-500 text-xs">{currentPage} / {totalPages} 페이지</p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
        >
          <i className="ri-arrow-left-s-line text-sm"></i>
        </button>
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
          const page = start + i;
          if (page > totalPages) return null;
          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-all ${
                currentPage === page
                  ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                  : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
              }`}
            >
              {page}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
        >
          <i className="ri-arrow-right-s-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

export default function ContactTable({
  contacts,
  groups,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  onEdit,
  onDelete,
  onSendTo,
  currentPage,
  totalPages,
  onPageChange,
}: Props) {
  const allSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const someSelected = contacts.some((c) => selectedIds.has(c.id));

  const getGroupCfg = (color: string) => GROUP_COLORS.find((c) => c.value === color) ?? GROUP_COLORS[0];

  if (contacts.length === 0) {
    return (
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl py-16 text-center">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-white/[0.04]">
          <i className="ri-user-search-line text-gray-600 text-3xl"></i>
        </div>
        <p className="text-gray-400 text-base font-medium mb-1">연락처가 없습니다</p>
        <p className="text-gray-600 text-sm">새 연락처를 추가하거나 CSV를 가져오세요</p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
      {/* ── Desktop Table ── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.07] bg-white/[0.02]">
              <th className="px-5 py-3.5 w-10">
                <button
                  onClick={onToggleAll}
                  className={`w-4 h-4 rounded border cursor-pointer transition-all flex items-center justify-center ${
                    allSelected
                      ? "bg-gradient-to-br from-violet-600 to-pink-600 border-transparent"
                      : someSelected
                      ? "bg-violet-500/30 border-violet-500/50"
                      : "border-white/[0.2] hover:border-white/[0.4]"
                  }`}
                >
                  {(allSelected || someSelected) && (
                    <i className={`${allSelected ? "ri-check-line" : "ri-subtract-line"} text-white text-xs`}></i>
                  )}
                </button>
              </th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">이름</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">전화번호</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">이메일</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">그룹</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">마지막 발송</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">메모</th>
              <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">액션</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, idx) => {
              const isSelected = selectedIds.has(c.id);
              const contactGroups = groups.filter((g) => c.groupIds.includes(g.id));
              return (
                <tr
                  key={c.id}
                  className={`border-b border-white/[0.04] transition-colors group ${
                    idx === contacts.length - 1 ? "border-b-0" : ""
                  } ${isSelected ? "bg-violet-500/[0.05]" : "hover:bg-white/[0.02]"}`}
                >
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => onToggleSelect(c.id)}
                      className={`w-4 h-4 rounded border cursor-pointer transition-all flex items-center justify-center ${
                        isSelected
                          ? "bg-gradient-to-br from-violet-600 to-pink-600 border-transparent"
                          : "border-white/[0.2] hover:border-white/[0.4]"
                      }`}
                    >
                      {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br ${getInitialColor(c.name)} text-white text-xs font-bold shrink-0`}>
                        {c.name.charAt(0)}
                      </div>
                      <span className="text-white text-sm font-medium whitespace-nowrap">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-gray-300 text-sm font-mono whitespace-nowrap">{c.phone}</span>
                  </td>
                  <td className="px-4 py-3.5 max-w-[160px]">
                    <span className="text-gray-400 text-sm truncate block">{c.email ?? "-"}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1 flex-wrap">
                      {contactGroups.length === 0 ? (
                        <span className="text-gray-600 text-xs">-</span>
                      ) : (
                        contactGroups.slice(0, 2).map((g) => {
                          const cfg = getGroupCfg(g.color);
                          return (
                            <span key={g.id} className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.bg} ${cfg.text}`}>
                              {g.name}
                            </span>
                          );
                        })
                      )}
                      {contactGroups.length > 2 && (
                        <span className="text-xs text-gray-500 whitespace-nowrap">+{contactGroups.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-gray-500 text-xs whitespace-nowrap">{c.lastSentAt ?? "-"}</span>
                  </td>
                  <td className="px-4 py-3.5 max-w-[140px]">
                    <span className="text-gray-500 text-xs truncate block">{c.memo ?? "-"}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onSendTo(c)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 cursor-pointer transition-all"
                        title="문자 발송"
                      >
                        <i className="ri-send-plane-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => onEdit(c)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-400 hover:text-white cursor-pointer transition-all"
                        title="수정"
                      >
                        <i className="ri-pencil-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => onDelete(c.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-400 hover:text-red-400 cursor-pointer transition-all"
                        title="삭제"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile Card View ── */}
      <div className="md:hidden divide-y divide-white/[0.05]">
        {contacts.map((c) => {
          const isSelected = selectedIds.has(c.id);
          const contactGroups = groups.filter((g) => c.groupIds.includes(g.id));
          return (
            <div
              key={c.id}
              className={`px-4 py-3 transition-colors ${isSelected ? "bg-violet-500/[0.06]" : ""}`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <button
                  onClick={() => onToggleSelect(c.id)}
                  className={`w-4 h-4 rounded border cursor-pointer transition-all flex items-center justify-center mt-0.5 shrink-0 ${
                    isSelected
                      ? "bg-gradient-to-br from-violet-600 to-pink-600 border-transparent"
                      : "border-white/[0.2] hover:border-white/[0.4]"
                  }`}
                >
                  {isSelected && <i className="ri-check-line text-white text-[10px]"></i>}
                </button>

                {/* Avatar + Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br ${getInitialColor(c.name)} text-white text-xs font-bold shrink-0`}>
                      {c.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{c.name}</p>
                      <p className="text-gray-400 text-xs font-mono">{c.phone}</p>
                    </div>
                  </div>

                  {/* Email */}
                  {c.email && (
                    <p className="text-gray-500 text-xs truncate mb-1 ml-10">{c.email}</p>
                  )}

                  {/* Groups */}
                  <div className="flex items-center gap-1 flex-wrap ml-10 mb-1.5">
                    {contactGroups.length === 0 ? (
                      <span className="text-gray-600 text-[10px]">그룹 없음</span>
                    ) : (
                      contactGroups.map((g) => {
                        const cfg = getGroupCfg(g.color);
                        return (
                          <span key={g.id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.bg} ${cfg.text}`}>
                            {g.name}
                          </span>
                        );
                      })
                    )}
                  </div>

                  {/* Meta row */}
                  <div className="flex items-center gap-3 ml-10 text-[10px] text-gray-500">
                    {c.lastSentAt && (
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-[10px]"></i>
                        {c.lastSentAt}
                      </span>
                    )}
                    {c.memo && (
                      <span className="truncate max-w-[120px]">{c.memo}</span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => onSendTo(c)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 cursor-pointer transition-all"
                  >
                    <i className="ri-send-plane-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onEdit(c)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-400 hover:text-white cursor-pointer transition-all"
                  >
                    <i className="ri-pencil-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onDelete(c.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-400 hover:text-red-400 cursor-pointer transition-all"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />}
    </div>
  );
}